import { Checkbox } from "flowbite-react";
import { Calendar1, Search, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Label } from "../../forms/Label";
import { Input } from "../../forms/Input";
import Select, { TSelectOption } from "@/components/forms/Select/Select";
import { TTableColumn } from "@/types/filter";
import { getDateFormatByLocale, safeParseDate } from "@/utils/commonUtils";
import DatePicker from "react-datepicker";
import { formatLocalDate } from "@/utils";

type TSearchProps = {
  searchType: string;
  setSearchType: (value: string) => void;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  handleSearch: () => void;
  tableColumns: TTableColumn[];
  defaultSearchType?: string;
  id?: string;
};

const SearchDropdown = ({
  searchType,
  setSearchType,
  searchQuery,
  setSearchQuery,
  handleSearch,
  tableColumns,
  defaultSearchType = "agent",
  id,
}: TSearchProps) => {
  const { t } = useTranslation("common");
  const hasActiveSearch = searchType && searchQuery;
  const currentColumnMeta = tableColumns.find((c) => c.id === searchType)?.meta;
  const searchTypeOptions: TSelectOption[] = tableColumns
    .filter(
      (col) =>
        col.id !== "actions" &&
        col.id !== "call_date" &&
        col.id !== "location" &&
        !col.meta?.excludeFromSearch &&
        col.id !== "status" &&
        col.id !== "has_user_account" &&
        col.id !== "service" &&
        col.id !== "created_on",
    )
    .map((col) => ({
      value: col.id,
      label:
        typeof col.header === "string" ? col.header : col.id.replace(/_/g, " "),
    }));

  const selectedSearchTypeOption =
    searchTypeOptions.find((opt) => opt.value === searchType) ?? null;

  const selectedOption =
    currentColumnMeta?.filterSearchOptions?.find(
      (opt) => opt.value === searchQuery,
    ) ?? null;

  const handleReviewStatusChange = (option: TSelectOption | null) => {
    setSearchQuery(option?.value ?? "");
  };

  const handleSearchTypeChange = (option: TSelectOption | null) => {
    setSearchType(option?.value ?? "");
    setSearchQuery("");
  };

  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  const handleOffensiveCheckbox = (value: string) => {
    if (searchQuery === value) {
      setSearchQuery("");
    } else {
      setSearchQuery(value);
    }
  };

  const isScoreNaPartialInput = (val: string) => {
    const lower = val.toLowerCase();
    return lower === "n" || lower === "na" || lower === "n/" || lower === "n/a";
  };

  // Clear search functionality
  const handleClearSearch = () => {
    setSearchType(defaultSearchType);
    setSearchQuery("");
    handleSearch(); // This will reset the filtered data
  };

  // Auto-search for certain field types when value changes
  useEffect(() => {
    if (searchType === "offensive_word_found" && searchQuery) {
      handleSearch();
    }
  }, [searchQuery, searchType, handleSearch]);

  // Handle Enter key press for text inputs
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const renderSearchInput = () => {
    if (currentColumnMeta?.filterSearchType === "date") {
      return (
        <div className="relative w-full">
          <DatePicker
            id={`${id}-date-search-input`}
            icon={<Calendar1 />}
            showMonthDropdown
            showYearDropdown
            autoComplete="off"
            dropdownMode="select"
            selected={safeParseDate(searchQuery)}
            onChange={(date: Date | null) => {
              if (date) {
                const formattedDate = formatLocalDate(date);
                setSearchQuery(formattedDate);
              }
            }}
            toggleCalendarOnIconClick
            showIcon
            placeholderText={getDateFormatByLocale("single", true)}
            dateFormat={getDateFormatByLocale()}
            onKeyDown={(e) => e.preventDefault()}
            className="block w-full dark:[color-scheme:dark] border-0 focus:outline-none focus:ring-0 focus:border-0"
          />
        </div>
      );
    }

    if (currentColumnMeta?.filterSearchType === "checkbox") {
      return (
        <div className="flex w-full items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id={`${id}-offensive-yes`}
              checked={searchQuery === "True"}
              onChange={() => handleOffensiveCheckbox("True")}
              data-testid="offensive-yes-checkbox"
            />
            <Label htmlFor={`${id}-offensive-yes`} weight={"medium"}>
              {t("YES")}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id={`${id}-offensive-no`}
              checked={searchQuery === "False"}
              onChange={() => handleOffensiveCheckbox("False")}
              data-testid="offensive-no-checkbox"
            />
            <Label htmlFor={`${id}-offensive-no`} weight={"medium"}>
              {t("NO")}
            </Label>
          </div>
        </div>
      );
    }

    if (currentColumnMeta?.filterSearchType === "dropdown") {
      return (
        <div className="relative w-full">
          <Select
            name="reviewStatus"
            options={currentColumnMeta?.filterSearchOptions ?? []}
            value={selectedOption}
            onChange={handleReviewStatusChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
            placeholder="Select..."
            className="w-full ai-review-filter-select"
            isClearable={false}
            isSearchable={false}
          />
        </div>
      );
    }

    return (
      <div className="relative w-full">
        <Input
          variant={"sm"}
          type={searchType?.toLowerCase().includes("email") ? "email" : "text"}
          placeholder={t("SEARCH")}
          value={searchQuery}
          autoComplete="off"
          onChange={(e) => {
            if (searchType === "ai_score" || searchType === "qa_score") {
              // Allow "N/A" (case insensitive) or numbers between 0 and 100
              let val = e.target.value.trim();

              // Allow N/A, NA, na, n/a (case insensitive) while typing
              if (isScoreNaPartialInput(val)) {
                setSearchQuery(val);
                return;
              }

              // If empty, clear search
              if (val === "") {
                setSearchQuery("");
                return;
              }

              // Otherwise, validate as number
              // Allow only up to 2 decimal places
              if (val?.includes(".")) {
                const [intPart, decPart] = val.split(".");
                val = intPart + "." + decPart.slice(0, 2);
              }

              const num = Number(val);
              // Regex to allow up to 2 decimal places
              const validDecimal = /^\d{0,3}(\.\d{0,2})?$/;
              if (
                !isNaN(num) &&
                num >= 0 &&
                num <= 100 &&
                validDecimal?.test(val)
              ) {
                setSearchQuery(val);
              }
            } else {
              // For email search, accept native email characters and do not strip
              if (searchType?.toLowerCase().includes("email")) {
                setSearchQuery(e.target.value);
                return;
              }
              // Text input: allow only letters, numbers, spaces, max 50 chars
              let val = e.target.value;
              val = val.replace(/[^a-zA-Z0-9 -]/g, "");
              if (val.length > 50) val = val.slice(0, 50);
              setSearchQuery(val);
            }
          }}
          onKeyPress={handleKeyPress}
          className="w-full border-0 focus:outline-none focus:ring-0 focus:border-0"
          id={`${id}-text-search-input`}
          aria-label="search query"
        />
        {!hasActiveSearch && (
          <button
            onClick={handleSearch}
            className="absolute right-2 top-1/2 -translate-y-1/2 transform rounded p-1"
            aria-label="search"
            id={`${id}-search-button`}
          >
            <Search className="h-4 w-4 text-foreground" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="w-full">
      <div
        className={`relative flex items-center border focus-within:border-primary pr-2 text-foreground w-full 
      search-dropdown ${isFocused ? "search-dropdown-focused" : ""}`}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        {/* Select Dropdown with Chevron */}
        <div className="relative flex items-center">
          <Select
            name="searchType"
            options={searchTypeOptions}
            value={selectedSearchTypeOption}
            onChange={handleSearchTypeChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
            placeholder="Select"
            className="text-sm"
            isClearable={false}
            isSearchable
          />
        </div>

        {/* Divider */}
        <div className="mx-2 h-8 w-px search-dropdown-divider" />

        {/* Search Input */}
        <div className="relative flex-1">{renderSearchInput()}</div>

        {/* Clear Search Button */}
        {hasActiveSearch && (
          <button
            onClick={handleClearSearch}
            className="absolute right-2 top-1/2 -translate-y-1/2 transform rounded-lg bg-background p-2 shadow-sm"
            aria-label="clear search"
            title="Clear search"
            style={{ zIndex: 2 }}
            id={`${id}-clear-search-button`}
          >
            <X className="h-4 w-4 text-foreground" />
          </button>
        )}
      </div>
    </div>
  );
};

export default SearchDropdown;
